import React from 'react';
import { NavLink } from 'react-router-dom';

import '@common/react/scss/components/menu.scss';

import { useMediaQuery } from '@app/hooks/useMediaQuery';
import AngleDownSVG from '@app/images/icons/AngleDownSVG';
import { CustomMenuItem } from '@app/objects/CustomMenuItem';

interface MenuItemProps extends Omit<MenuProps, 'items'> {
	item: CustomMenuItem;
	closeMenu: () => void;
	withChildren?: boolean;
	basePath?: string;
	pathKey?: string;
	onChangeVisibleModal?: (e: string) => void;
}

interface MenuProps {
	items: Array<CustomMenuItem>;
	closeMenu: () => void;
	defaultOpen?: boolean;
	onChangeVisibleModal?: (e: string) => void;
}

const arrowIcon = require('@app/images/arrow_circle.svg');
const arrowIconActive = require('@app/images/arrow_circle_active.svg');

const SwitchItem = ({
	item,
	switchTab,
	withIcon,
	path,
	isActive,
	exact,
	index,
	currentIndex,
}) => {
	return (
		<div className={`menu-component__tab-item ${currentIndex === index ? 'menu-component__tab-item_active' : ''}`} style={{ display: 'flex' }}>
			<div onMouseEnter={() => switchTab(index)} className={`menu-component__tab-item-btn ${withIcon ? 'btn-text' : ''}`}>
				{item.name}
				{item.withIcon && (
					<img
						className={`arrow-icon ${currentIndex === index ? 'arrow-icon_active' : ''}`}
						src={currentIndex === index ? arrowIconActive : arrowIcon}
						alt="Arrow"
					/>
				)}
			</div>
			<div className="menu-component__tab-item-children" style={{ justifyContent: 'space-between' }}>
				{item.children?.map((list, listIndex) => (
					<div
						key={listIndex}
						className={`menu-component__tab-item-list ${item.name.toLowerCase()}`}
						style={{ display: 'flex', flexDirection: 'column' }}
					>
						{list.map((child, index) => {
							return child.path ? (
								!child.externalLink ? (
									<NavLink
										to={child.path}
										key={index}
										className={({ isActive }) => `menu-component__item-link ${isActive ? 'menu-component__item-link_active' : ''}`}
									>
										{child.name}
									</NavLink>
								) : (
									<a
										key={index}
										href={child.path}
										target={child.newTab ? '_blank' : '_self'}
										rel="noreferrer noopener"
										className="menu-component__item-link"
									>
										{child.name}
									</a>
								)
							) : (
								<span key={index} className="menu-component__item-title">
									{child.name}
								</span>
							);
						})}
					</div>
				))}
			</div>
		</div>
	);
};

const Item: React.FC<MenuItemProps> = (props) => {
	const {
		item,
		withChildren,
		basePath,
		pathKey,
		closeMenu,
		onChangeVisibleModal,
	} = props;
	const [open, setOpen] = React.useState<boolean>(props.defaultOpen || item.isOpen || false);
	const isLargeScreen = useMediaQuery(1400);

	const toggleMenu = (): void => {
		setOpen((prev) => !prev);
	};

	const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

	const condition = withChildren && item.children?.length;
	const path = item[pathKey || 'path'];
	const {
		exact = false,
		isActive,
		childrenFullWidth,
		withIcon,
		externalLink,
		newTab,
		switcher,
	} = item;

	const className = `menu-component__item ${open && condition ? 'menu-component__item_open' : ''} ${
		condition ? 'menu-component__item_with-children' : ''
	} ${item.className || ''}`;
	const containerClassName = childrenFullWidth ? 'menu-component__item-children-container_full-width' : '';

	const [currentIndex, setCurrentIndex] = React.useState(0);

	const switchTab = (index) => {
		setCurrentIndex(index);
	};

	return (
		<li className={className} onClick={toggleMenu}>
			{path ? (
				!externalLink ? (
					<NavLink
						to={path}
						className={({ isActive }) => `menu-component__item-link ${isActive ? 'menu-component__item-link_active' : ''} ${withIcon ? 'btn-text' : ''}`}
						onClick={(e) => {
							if (item.modal && onChangeVisibleModal) {
								e.preventDefault();
								onChangeVisibleModal && onChangeVisibleModal(item.modal);
								closeMenu();
							}
						}}
					>
						{item.name}
						{withIcon && <img className="arrow-icon" src={arrowIcon} alt="Arrow" />}
					</NavLink>
				) : (
					<a href={path} target={newTab ? '_blank' : '_self'} rel="noreferrer noopener" className={`menu-component__item-link ${withIcon ? 'btn-text' : ''}`}>
						{item.name}
						{withIcon && <img className="arrow-icon" src={arrowIcon} alt="Arrow" />}
					</a>
				)
			) : (
				<span
					className="menu-component__item-link"
					onClick={() => {
						if (item.modal && onChangeVisibleModal) {
							onChangeVisibleModal && onChangeVisibleModal(item.modal);
							closeMenu();
						}
					}}
				>
					{item.name}
				</span>
			)}
			{condition && !switcher && (
				<>
					<span className="menu-component__item-carrot">
						<AngleDownSVG className={`menu-component__item-carrot_${isLargeScreen && open ? 'up' : 'down'}`} />
					</span>
					<div
						className={`menu-component__item-children-container ${open ? 'isOpen' : ''} ${containerClassName}`}
						onClick={stopPropagation}
					>
						{item.withSwitcher && !isLargeScreen ? (
							<div className="menu-component__tab-container">
								{item.children?.map((list, listIndex) => (
									<React.Fragment key={`list-${listIndex}`}>
										{list.map((child, index) => (
											<SwitchItem
												item={child}
												switchTab={switchTab}
												withIcon={withIcon}
												path={path}
												isActive={isActive}
												exact={exact}
												key={index}
												index={index}
												currentIndex={currentIndex}
											/>
										))}
									</React.Fragment>
								))}
							</div>
						) : (
							item.children?.map((list, listIndex) => (
								<ul className="menu-component__children" key={listIndex} style={{ width: '50%' }}>
									{list.map((child, index) => {
										if (child.modal) {
											return (
												<li key={index} className="menu-component__item">
													<span
														className="menu-component__item-link"
														onClick={(e) => {
															e.preventDefault();
															onChangeVisibleModal && onChangeVisibleModal(child.modal as string);
															closeMenu();
														}}
													>
														{child.name}
													</span>
												</li>
											);
										}

										return <Item item={child} key={index} withChildren={withChildren} pathKey={pathKey} closeMenu={closeMenu} />;
									})}
								</ul>
							))
						)}
					</div>
				</>
			)}
		</li>
	);
};

const CustomMenu: React.FC<MenuProps> = ({
	items,
	defaultOpen,
	closeMenu,
	onChangeVisibleModal,
}) => {
	const menuItems = items || [];

	return (
		<ul className="menu-component">
			{menuItems.map((item, index) => (
				<Item defaultOpen={defaultOpen} key={index} item={item} withChildren={!!item.children} closeMenu={closeMenu} onChangeVisibleModal={onChangeVisibleModal && onChangeVisibleModal} />
			))}
		</ul>
	);
};

export default CustomMenu;
